/*
 * Choosing the article's product. Exactly one, always one.
 *
 * A RADIO GROUP RATHER THAN THE CHECKBOX LIST BESIDE IT, and the reason is in
 * the schema rather than in taste: kb.Article.ProductId is a NOT NULL column
 * with a foreign key, not a junction row, so "none" and "two" are states the
 * database refuses outright. A checkbox list can express both of them, and a
 * control that can express a state the store cannot hold spends its life
 * reporting errors for shapes it offered in the first place. Radios cannot
 * produce either: one is checked, and clicking another moves the choice instead
 * of adding to it.
 *
 * It wears the checkbox picker's clothes on purpose - the same .tag-picker box,
 * the same .checkbox-list rows, the same "(retired)" marking, the same empty
 * state. The two controls sit one above the other in the Properties card, and
 * the only difference between them that an author needs to see is how many
 * things can be ticked; a product picker that looked like a different KIND of
 * control would suggest a difference that is not there.
 *
 * Inactive products are shown only when already assigned, exactly as
 * TagMultiSelect does it - and here the rule is load-bearing rather than a
 * courtesy. An article cannot be saved without a product, so dropping a retired
 * one from the list would leave the author unable to save at all without also
 * moving the article to some other product they never chose. It is still not
 * offered for a fresh assignment.
 *
 * No `grow` prop. TagMultiSelect's comment already says why the product picker
 * would not want it: a handful of entries, so the spare height of the card would
 * be a taller empty box. The long list is the category one, and it keeps that.
 *
 * Its words come from the chosen language's dictionary, `t.admin.articles` (S48).
 */

import { useId } from 'react';
import { useMessages } from '../../i18n/language.js';
import type { TagOption } from './TagMultiSelect.js';

export function ProductSelect({
  options,
  selected,
  onChange,
  error,
  name = 'article-product',
}: {
  /**
   * The radios' shared name, which is what groups them for the keyboard. The
   * release-note forms (S42, 2026-10-12) pass their own, so the group never
   * reads as an article's in the DOM.
   */
  name?: string;
  options: TagOption[];
  /**
   * Null only before anything has been chosen, which happens on the new-article
   * page and nowhere else: an existing article always has a product, because the
   * column it comes from cannot be null.
   */
  selected: number | null;
  /** Never called with null: a radio group has no gesture for un-choosing. */
  onChange: (next: number) => void;
  error?: string | undefined;
}): JSX.Element {
  const words = useMessages().admin.articles.tags;
  const labelId = useId();
  const visible = options.filter((option) => option.isActive || option.id === selected);

  return (
    <div className={`field${error ? ' field-invalid' : ''}`}>
      <label id={labelId}>
        {words.product}
        <span aria-hidden="true"> *</span>
      </label>

      {/*
        The label above names a group, not a control, so it has no `for` to point
        at and nothing would announce it. role="radiogroup" plus aria-labelledby
        is what carries it across. The shared `name` on the inputs is what groups
        them for the KEYBOARD - that is native behaviour and is not what the role
        is doing here.
      */}
      <div className="tag-picker" role="radiogroup" aria-labelledby={labelId}>
        {visible.length === 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: '0.875rem' }}>
            {words.noTags('product')}
          </p>
        ) : (
          <ul className="checkbox-list">
            {visible.map((option) => (
              <li key={option.id}>
                <label>
                  <input
                    type="radio"
                    name={name}
                    checked={option.id === selected}
                    onChange={() => onChange(option.id)}
                  />
                  <span>
                    {option.name}
                    {!option.isActive && <span className="faint"> {words.retired}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && (
        <p className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
